import type { ModuleColorClass } from './types'

// Single source of truth for per-module colour. Each module's hero banner sets
// the tone; every accent on its page (icons, eyebrows, stat numbers, callout,
// CTA, tier badge) is tinted to match. Class names are full literals so
// Tailwind's content scanner generates them.
export interface ModuleTheme {
  /** Hero banner gradient: dark centre keeps white text/badge readable. */
  heroBackground: string
  /** Title accent word on the dark hero. */
  heroAccent: string
  /** Accent text on light backgrounds (eyebrows, stat values, CTA accent, links). */
  accentText: string
  /** Icon chip background. */
  iconBg: string
  /** Stats callout background + border. */
  statCard: string
  /** Tier badge (card variant) text + bg + border. */
  badge: string
  /** Primary CTA button background override ('' keeps the default brand teal). */
  ctaButton: string
}

export const MODULE_THEME: Record<ModuleColorClass, ModuleTheme> = {
  meeting: {
    heroBackground: 'linear-gradient(135deg, #0f766e 0%, #0f172a 52%, #065f46 100%)',
    heroAccent: 'text-teal-300',
    accentText: 'text-teal-700',
    iconBg: 'bg-teal-50',
    statCard: 'bg-teal-50 border-teal-200',
    badge: 'text-teal-700 bg-teal-50 border-teal-100',
    ctaButton: '',
  },
  referral: {
    heroBackground: 'linear-gradient(135deg, #db2777 0%, #0f172a 55%, #831843 100%)',
    heroAccent: 'text-pink-300',
    accentText: 'text-pink-600',
    iconBg: 'bg-pink-50',
    statCard: 'bg-pink-50 border-pink-200',
    badge: 'text-pink-700 bg-pink-50 border-pink-100',
    ctaButton: '!bg-button-pink',
  },
  growth: {
    heroBackground: 'linear-gradient(135deg, #d97706 0%, #0f172a 55%, #7c2d12 100%)',
    heroAccent: 'text-amber-300',
    accentText: 'text-amber-600',
    iconBg: 'bg-amber-50',
    statCard: 'bg-amber-50 border-amber-200',
    badge: 'text-amber-700 bg-amber-50 border-amber-100',
    ctaButton: '!bg-button-amber',
  },
  ramble: {
    heroBackground: 'linear-gradient(135deg, #7c3aed 0%, #0f172a 55%, #4c1d95 100%)',
    heroAccent: 'text-violet-300',
    accentText: 'text-violet-600',
    iconBg: 'bg-violet-50',
    statCard: 'bg-violet-50 border-violet-200',
    badge: 'text-violet-700 bg-violet-50 border-violet-100',
    ctaButton: '!bg-button-violet',
  },
  portal: {
    heroBackground: 'linear-gradient(135deg, #4f46e5 0%, #0f172a 55%, #312e81 100%)',
    heroAccent: 'text-indigo-300',
    accentText: 'text-indigo-600',
    iconBg: 'bg-indigo-50',
    statCard: 'bg-indigo-50 border-indigo-200',
    badge: 'text-indigo-700 bg-indigo-50 border-indigo-100',
    ctaButton: '!bg-button-indigo',
  },
  headroom: {
    heroBackground: 'linear-gradient(135deg, #059669 0%, #0f172a 55%, #064e3b 100%)',
    heroAccent: 'text-emerald-300',
    accentText: 'text-emerald-600',
    iconBg: 'bg-emerald-50',
    statCard: 'bg-emerald-50 border-emerald-200',
    badge: 'text-emerald-700 bg-emerald-50 border-emerald-100',
    ctaButton: '!bg-button-emerald',
  },
}
